import React, { useEffect, useRef, useState } from "react";
import {
  DirectionsRenderer,
  GoogleMap,
  MarkerF,
  OverlayView,
  OverlayViewF,
  TrafficLayer,
  PolylineF,
  PolygonF,
  useJsApiLoader,
} from "@react-google-maps/api";
import { SmallLoader } from "../components/common/SmallLoader";
import useUtils from "../hooks/useUtils";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import useLanguage from "../hooks/useLanguage";
import DriverMarkers from "./DriverMarker";
import LiveRoute from "./LiveRoute";
import useTheme from "../hooks/useTheme";
import { useThemes } from "../contexts/ThemeContext";
import { useRecoilValue } from "recoil";
import { settingsAtom } from "../state";

const LIBRARIES = ["drawing", "places", "geometry"];

const FaCheckCircle = ({ style }) => {
  return (
    <svg
      style={style}
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 512 512"
      height="16"
      width="16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path>
    </svg>
  );
};

const AiFillCloseCircle = ({ style, size }) => {
  return (
    <svg
      style={style}
      fontSize={size}
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 1024 1024"
      fillRule="evenodd"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M512 64c247.4 0 448 200.6 448 448S759.4 960 512 960 64 759.4 64 512 264.6 64 512 64Zm127.978 274.82-.034.006c-.023.007-.042.018-.083.059L512 466.745l-127.86-127.86c-.042-.041-.06-.052-.084-.059a.118.118 0 0 0-.07 0c-.022.007-.041.018-.082.059l-45.02 45.019c-.04.04-.05.06-.058.083a.118.118 0 0 0 0 .07l.01.022a.268.268 0 0 0 .049.06L466.745 512l-127.86 127.862c-.041.04-.052.06-.059.083a.118.118 0 0 0 0 .07c.007.022.018.041.059.082l45.019 45.02c.04.04.06.05.083.058a.118.118 0 0 0 .07 0c.022-.007.041-.018.082-.059L512 557.254l127.862 127.861c.04.041.06.052.083.059a.118.118 0 0 0 .07 0c.022-.007.041-.018.082-.059l45.02-45.019c.04-.04.05-.06.058-.083a.118.118 0 0 0 0-.07l-.01-.022a.268.268 0 0 0-.049-.06L557.254 512l127.861-127.86c.041-.042.052-.06.059-.084a.118.118 0 0 0 0-.07c-.007-.022-.018-.041-.059-.082l-45.019-45.02c-.04-.04-.06-.05-.083-.058a.118.118 0 0 0-.07 0Z"></path>
    </svg>
  );
};

const MarkerPop = ({ marker, content, clickedClose }) => {
  const { theme } = useTheme();
  const { themes, currentTheme } = useThemes();
  const themeT = themes[currentTheme];
  return (
    <div
      className={`absolute p-3 px-2 ${themeT.colors.blocksBg} ${themeT.colors.shadow} rounded-lg -translate-x-1/2 -translate-y-full mb-2`}
      style={{
        zIndex: 10000,
        top: "-20px",
        left: "50%",
        minWidth: "200px",
      }}
    >
      <div className=" ">
        <div className="flex items-center gap-3 mt-3 w-full">
          {marker}
          <p>{content}</p>
        </div>
        <button
          className="absolute top-0 right-0 bg-gray-200 dark:bg-slate-600 rounded-full w-4 h-4 flex items-center justify-center text-xs"
          onClick={(e) => {
            e.stopPropagation();
            clickedClose();
          }}
          style={{
            top: "5px",
            right: "5px",
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

const AcceptIconMarker = ({ isPop, clicked }) => {
  const { theme } = useTheme();
  const { themes, currentTheme } = useThemes();
  const themeT = themes[currentTheme];
  return (
    <div>
      <div
        className={`relative  bg-black dark:bg-slate-600  rounded-full shadow-md flex items-center justify-center w-[13px] h-[13px]`}
        onClick={() => isPop && clicked()}
      >
        <div className={` bg-white  rounded-full w-[4px] h-[4px]`}></div>
      </div>
    </div>
  );
};

const ArriveIconMarker = ({ isPop, clicked, map }) => {
  return (
    <div>
      <p
        onClick={() => isPop && clicked()}
        className={`${map ? "bg-white dark:text-black p-1" : "dark:text-white"}  rounded-full `}
      >
        <svg
          stroke="currentColor"
          fill="currentColor"
          strokeWidth="0"
          viewBox="0 0 24 24"
          height="16px"
          width="16px"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM13 12H17V14H11V7H13V12Z"></path>
        </svg>
      </p>
    </div>
  );
};

const PickupEstimatedIconMarker = ({ isPop, clicked }) => {
  return (
    <div
      className="h-5 w-5 flex justify-center items-center text-white rounded-full"
      style={{ backgroundColor: "#5e35b1" }}
      onClick={() => isPop && clicked()}
    >
      <p className="h-5 w-5 flex justify-center items-center">{"A"}</p>
    </div>
  );
};

const PickupFinalIconMarker = ({ isPop, clicked }) => {
  return (
    <div
      onClick={() => isPop && clicked()}
      className="h-5 w-5 text-xs flex justify-center items-center text-white rounded-full"
      style={{ backgroundColor: "#607d8b" }}
    >
      <p className="h-5 w-5 flex justify-center items-center">{"P"}</p>
    </div>
  );
};

const StopsIconMarker = ({ isPop, clicked, value }) => {
  return (
    <div
      className="relative  rounded-full shadow-md flex items-center justify-center bg-[#e3f2fd] dark:text-black"
      style={{ width: "19px", height: "19px", border: "2.8px solid #2196f3" }}
      onClick={() => {
        isPop && clicked();
      }}
    >
      <p className="w-[19px] h-[19px] flex justify-center items-center">
        {value}
      </p>
    </div>
  );
};

const DropEstimatedIconMarker = ({ isPop, clicked }) => {
  return (
    <div
      className="h-5 w-5 flex justify-center items-center text-white rounded-full"
      style={{ backgroundColor: "#3f51b5" }}
      onClick={() => {
        isPop && clicked();
      }}
    >
      <p className="h-5 w-5 flex justify-center items-center">{"B"}</p>
    </div>
  );
};

const DropFinalIconMarker = ({ isPop, clicked }) => {
  return (
    <div
      className="h-5 w-5 flex justify-center items-center text-white rounded-full"
      style={{ backgroundColor: "#43a047" }}
      onClick={() => {
        if (isPop) clicked();
      }}
    >
      <p className="h-5 w-5 flex justify-center items-center">D</p>
    </div>
  );
};

function GoogleMapComponent({
  value,
  liveData,
  onGoingWayData,
  onGoingWayDataAfterPicked,
  tollsData,
  mobile,
  estimation,
  driverProfile,
  passengerProfile,
  contactSection,
  hideHeader = () => {},
}) {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const { themes, currentTheme } = useThemes();
  const themeT = themes[currentTheme];
  const { pathname } = useLocation();
  const settings = useRecoilValue(settingsAtom);
  const { mapStyles, darkModeMapStyles, formatTimeFromSeconds } = useUtils();
  const [map, setMap] = useState(null);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const directionsRef = useRef(null);
  const [directionRoutePoints, setDirectionRoutePoints] = React.useState();
  const [manualWayData, setManualWayData] = React.useState();
  const [zoomLevel, setZoomLevel] = useState(10);
  const [expectedPickup, setExpectedPickup] = useState();
  const [selectedTab, setSelectedTab] = useState(1);
  const [curvedPath, setCurvedPath] = useState([]);
  const [interruptedPath, setInterruptedPath] = useState([]);
  const [stopsData, setStopsData] = useState([]);
  const [enableTraffic, setEnableTraffic] = useState(false);
  const [showPopPick, setShowPopPick] = useState(false);
  const [showPopDrop, setShowPopDrop] = useState(false);
  const [showPopDropIndex, setShowPopDropIndex] = useState(0);
  const [showPopAccept, setShowPoAccept] = useState(false);
  const [showPopArrive, setShowPopArrive] = useState(false);
  const [showPopStart, setShowPopStart] = useState(false);
  const [showPopFinalDrop, setShowFinalDrop] = useState(false);
  const [locationUpdateTime, setlocationUpdateTime] = useState();
  let driverButtonRef = React.useRef(null);
  const endStates = [
    "ENDED",
    "PROFESSIONALCANCELLED",
    "USERCANCELLED",
    "USERDENIED",
    "EXPIRED",
    "AWAITING",
    "NEW",
  ];
  //
  const [showTolls, setShowTolls] = useState(false);
  //----

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: settings?.mapApi?.web,
    libraries: LIBRARIES,
  });
  //-----

  const historySectionRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!historySectionRef.current) return;

      const rect = historySectionRef.current.getBoundingClientRect();
      //console.log(rect.top, "lkujhg");
      // When section top touches viewport top
      if (rect.top <= 62) {
        hideHeader(true);
      } else {
        hideHeader(false);
      }
      setIsScrolled(rect.top <= 62);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  let pickupPoint =
    value?.bookingStatus === "ENDED"
      ? value?.activity?.rideStops?.[0]
      : value?.rideStops?.[0];

  let dropPoint =
    value?.bookingStatus === "ENDED"
      ? value?.activity?.rideStops?.slice(1)
      : value?.rideStops?.slice(1);

  //
  // Function to calculate distance between two lat/lng points using Haversine formula
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  // Function to find nearest point in originalWayData for each interrupted point in manualWayData
  function calculateDistanceWay(lat1, lon1, lat2, lon2) {
    const R = 6371000; // meters
    const toRad = (val) => (val * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  const getFinalTripPath = (
    manualWayData = [],
    snapWayData = [],
    threshold = 100,
  ) => {
    // If no snap data → just return manual GPS
    if (!snapWayData.length) return manualWayData;

    const finalPath = manualWayData.map((manualPoint) => {
      let nearestSnap = null;
      let minDistance = Infinity;

      // Find closest snap point
      snapWayData.forEach((snapPoint) => {
        const distance = calculateDistanceWay(
          manualPoint.lat,
          manualPoint.lng,
          snapPoint.lat,
          snapPoint.lng,
        );
        if (distance < minDistance) {
          minDistance = distance;
          nearestSnap = snapPoint;
        }
      });

      // Case 1: It's an interruption/stop → always trust manual
      if (manualPoint.isInterrupt || manualPoint.isStop) {
        return manualPoint;
      }

      // Case 2: If snap is close enough → use snapped coords + manual flags
      if (nearestSnap && minDistance <= threshold) {
        return {
          ...manualPoint, // keep manual timestamp, flags
          lat: nearestSnap.lat, // replace with snapped coords
          lng: nearestSnap.lng,
        };
      }

      // Case 3: Snap too far → fallback to raw manual
      return manualPoint;
    });

    return finalPath;
  };

  const actualPathWayData =
    value?.originalWayData?.length > 0
      ? getFinalTripPath(value.manualWayData, value?.originalWayData)
      : value.manualWayData;

  //console.log(pickupPoint, dropPoint, "kjfe");

  const handleZoomChanged = () => {
    if (mapRef.current) {
      setZoomLevel(mapRef.current.getZoom());
    }
  };

  const handleMapLoad = (map) => {
    const pickup = {
      lat:
        value?.bookingStatus === "ENDED"
          ? value?.pickupLocation?.coordinates?.[0]?.[1]
          : value.rideStops[0].lat,
      lng:
        value?.bookingStatus === "ENDED"
          ? value?.pickupLocation?.coordinates?.[0]?.[0]
          : value.rideStops[0].lng,
    };
    const dropoff = {
      lat:
        value?.bookingStatus === "ENDED"
          ? value?.dropLocation?.coordinates?.[0]?.[1]
          : value.rideStops[value.rideStops.length - 1].lat,
      lng:
        value?.bookingStatus === "ENDED"
          ? value?.dropLocation?.coordinates?.[0]?.[0]
          : value.rideStops[value.rideStops.length - 1].lng,
    };

    mapRef.current = map;
    setMap(map);

    if (value?.rideCoordinatesEncoded) {
      const path = window.google.maps.geometry.encoding.decodePath(
        value?.rideCoordinatesEncoded,
      );
      setDirectionRoutePoints(path);
      let googleBounds = new window.google.maps.LatLngBounds();

      path.forEach((coord) => {
        googleBounds.extend(coord);
      });
      if (liveData) {
        googleBounds.extend(
          new window.google.maps.LatLng(liveData?.lat, liveData?.lng),
        );
      }
      if (value?.activity?.acceptLatLng?.lat) {
        googleBounds.extend(
          new window.google.maps.LatLng(
            value?.activity?.acceptLatLng?.lat,
            value?.activity?.acceptLatLng?.lng,
          ),
        );
      }
      if (value?.activity?.arriveLatLng?.lat) {
        googleBounds.extend(
          new window.google.maps.LatLng(
            value?.activity?.arriveLatLng?.lat,
            value?.activity?.arriveLatLng?.lng,
          ),
        );
      }
      if (value?.activity?.pickUpLatLng?.lat) {
        googleBounds.extend(
          new window.google.maps.LatLng(
            value?.activity?.pickUpLatLng?.lat,
            value?.activity?.pickUpLatLng?.lng,
          ),
        );
      }
      if (value?.manualWayData?.length > 0) {
        value?.manualWayData?.forEach((coord) => {
          googleBounds.extend(coord);
        });
      }
      if (value?.pickupWayData?.length > 0) {
        value?.pickupWayData?.forEach((coord) => {
          googleBounds.extend(coord);
        });
      }
      if (onGoingWayData?.length > 0) {
        onGoingWayData?.forEach((coord) => {
          googleBounds.extend(coord);
        });
      }
      map.fitBounds(googleBounds);
      //map.setZoom(map.getZoom() - 1);
    } else {
      let googleBounds = new window.google.maps.LatLngBounds();
      googleBounds.extend(
        new window.google.maps.LatLng(pickup.lat, pickup.lng),
      );
      googleBounds.extend(
        new window.google.maps.LatLng(dropoff.lat, dropoff.lng),
      );
      if (liveData) {
        googleBounds.extend(
          new window.google.maps.LatLng(liveData?.lat, liveData?.lng),
        );
      }
      if (value?.activity?.acceptLatLng?.lat) {
        googleBounds.extend(
          new window.google.maps.LatLng(
            value?.activity?.acceptLatLng?.lat,
            value?.activity?.acceptLatLng?.lng,
          ),
        );
      }
      if (value?.activity?.arriveLatLng?.lat) {
        googleBounds.extend(
          new window.google.maps.LatLng(
            value?.activity?.arriveLatLng?.lat,
            value?.activity?.arriveLatLng?.lng,
          ),
        );
      }
      if (value?.activity?.pickUpLatLng?.lat) {
        googleBounds.extend(
          new window.google.maps.LatLng(
            value?.activity?.pickUpLatLng?.lat,
            value?.activity?.pickUpLatLng?.lng,
          ),
        );
      }
      if (value?.manualWayData?.length > 0) {
        value?.manualWayData?.forEach((coord) => {
          googleBounds.extend(coord);
        });
      }
      if (value?.pickupWayData?.length > 0) {
        value?.pickupWayData?.forEach((coord) => {
          googleBounds.extend(coord);
        });
      }
      if (onGoingWayData?.length > 0) {
        onGoingWayData?.forEach((coord) => {
          googleBounds.extend(coord);
        });
      }
      map.fitBounds(googleBounds);
      // map.fitBounds(bounds, {
      //   padding: { top: 50, right: 50, bottom: 50, left: 50 }
      // });
      map.setZoom(map.getZoom() - 1);
    }
  };

  const directionRoute = async (originLat, originLng, destinations, type) => {
    const DirectionService = new window.google.maps.DirectionsService();
    DirectionService.route(
      {
        origin: { lat: originLat, lng: originLng },
        destination: {
          lat: destinations[destinations?.length - 1]?.lat,
          lng: destinations[destinations?.length - 1]?.lng,
        },
        waypoints: destinations
          .slice(0, destinations.length - 1)
          .map((destination) => ({
            location: new window.google.maps.LatLng(
              destination?.lat,
              destination?.lng,
            ),
          })),
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
        provideRouteAlternatives: true,
        optimizeWaypoints: true,
      },
      async (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          if (directionsRef.current) {
            directionsRef.current.setDirections(result);
          }
          //console.log(result, "jwkbevh");
          if (type === "EXPECTEDPICKUPTIME") {
            //console.log(result, "jwkbevh");
            setExpectedPickup(result?.routes[0]?.legs[0]?.duration);
          } else {
            //setDirectionRoutePoints(result);
            setExpectedPickup();
          }
        } else {
          console.error("Error in direction path");
          setExpectedPickup();
        }
      },
    );
  };

  const centerMap = () => {
    if (!mapRef.current || !value?.rideStops) return;

    const pickup = {
      lat:
        value?.bookingStatus === "ENDED"
          ? value?.pickupLocation?.coordinates?.[0]?.[1]
          : value.rideStops[0].lat,
      lng:
        value?.bookingStatus === "ENDED"
          ? value?.pickupLocation?.coordinates?.[0]?.[0]
          : value.rideStops[0].lng,
    };
    const dropoff = {
      lat:
        value?.bookingStatus === "ENDED"
          ? value?.dropLocation?.coordinates?.[0]?.[1]
          : value.rideStops[value.rideStops.length - 1].lat,
      lng:
        value?.bookingStatus === "ENDED"
          ? value?.dropLocation?.coordinates?.[0]?.[0]
          : value.rideStops[value.rideStops.length - 1].lng,
    };

    if (value?.rideCoordinatesEncoded) {
      const path = window.google.maps.geometry.encoding.decodePath(
        value?.rideCoordinatesEncoded,
      );
      setDirectionRoutePoints(path);
      let googleBounds = new window.google.maps.LatLngBounds();

      path.forEach((coord) => {
        googleBounds.extend(coord);
      });
      if (liveData) {
        googleBounds.extend(
          new window.google.maps.LatLng(liveData?.lat, liveData?.lng),
        );
      }
      if (value?.activity?.acceptLatLng?.lat) {
        googleBounds.extend(
          new window.google.maps.LatLng(
            value?.activity?.acceptLatLng?.lat,
            value?.activity?.acceptLatLng?.lng,
          ),
        );
      }
      if (value?.activity?.arriveLatLng?.lat) {
        googleBounds.extend(
          new window.google.maps.LatLng(
            value?.activity?.arriveLatLng?.lat,
            value?.activity?.arriveLatLng?.lng,
          ),
        );
      }
      if (value?.activity?.pickUpLatLng?.lat) {
        googleBounds.extend(
          new window.google.maps.LatLng(
            value?.activity?.pickUpLatLng?.lat,
            value?.activity?.pickUpLatLng?.lng,
          ),
        );
      }
      if (value?.manualWayData?.length > 0) {
        value?.manualWayData?.forEach((coord) => {
          googleBounds.extend(coord);
        });
      }
      if (value?.pickupWayData?.length > 0) {
        value?.pickupWayData?.forEach((coord) => {
          googleBounds.extend(coord);
        });
      }
      if (onGoingWayData?.length > 0) {
        onGoingWayData?.forEach((coord) => {
          googleBounds.extend(coord);
        });
      }
      map.fitBounds(googleBounds);
      //map.setZoom(map.getZoom() - 1);
    } else {
      let googleBounds = new window.google.maps.LatLngBounds();
      googleBounds.extend(
        new window.google.maps.LatLng(pickup.lat, pickup.lng),
      );
      googleBounds.extend(
        new window.google.maps.LatLng(dropoff.lat, dropoff.lng),
      );
      if (liveData) {
        googleBounds.extend(
          new window.google.maps.LatLng(liveData?.lat, liveData?.lng),
        );
      }
      if (value?.activity?.acceptLatLng?.lat) {
        googleBounds.extend(
          new window.google.maps.LatLng(
            value?.activity?.acceptLatLng?.lat,
            value?.activity?.acceptLatLng?.lng,
          ),
        );
      }
      if (value?.activity?.arriveLatLng?.lat) {
        googleBounds.extend(
          new window.google.maps.LatLng(
            value?.activity?.arriveLatLng?.lat,
            value?.activity?.arriveLatLng?.lng,
          ),
        );
      }
      if (value?.activity?.pickUpLatLng?.lat) {
        googleBounds.extend(
          new window.google.maps.LatLng(
            value?.activity?.pickUpLatLng?.lat,
            value?.activity?.pickUpLatLng?.lng,
          ),
        );
      }
      if (value?.manualWayData?.length > 0) {
        value?.manualWayData?.forEach((coord) => {
          googleBounds.extend(coord);
        });
      }
      if (value?.pickupWayData?.length > 0) {
        value?.pickupWayData?.forEach((coord) => {
          googleBounds.extend(coord);
        });
      }
      if (onGoingWayData?.length > 0) {
        onGoingWayData?.forEach((coord) => {
          googleBounds.extend(coord);
        });
      }
      map.fitBounds(googleBounds);
    }
  };

  const getAdjustedPosition = (lat, lng, index) => {
    const offset = 0.00001 * (index + 0.2); // Adjust the offset as needed
    return {
      // lat: lat + offset * Math.cos((index * Math.PI) / 4),
      // lng: lng + offset * Math.sin((index * Math.PI) / 4),
      lat: lat + offset * Math.cos((index * Math.PI) / 4),
      lng: lng + offset * Math.sin((index * Math.PI) / 4),
    };
  };

  const [blinkingToll, setBlinkingToll] = useState(null);

  const handleTollNameClick = (tollId, index) => {
    setBlinkingToll((prev) => {
      if (prev === tollId || prev === index) {
        return null;
      } else {
        return tollId || index;
      }
    });
    //setTimeout(() => setBlinkingToll(null), 1500); // Stop blinking after 1.5 seconds
  };

  const checkAcceptToPickup = () => {
    if (!expectedPickup?.value) {
      return;
    }

    const acceptTime = new Date(value?.activity?.acceptTime);
    const arriveTime = new Date(value?.activity?.arriveTime);

    const targetTime = new Date(
      acceptTime.getTime() + expectedPickup?.value * 1000,
    );

    acceptTime.setSeconds(0, 0);
    arriveTime.setSeconds(0, 0);
    targetTime.setSeconds(0, 0);

    // Calculate the difference between actual arrival and target time in minutes
    const diffInMilliseconds = arriveTime.getTime() - targetTime.getTime();
    const diffInMinutes = Math.round(diffInMilliseconds / (1000 * 60));

    // Define a small buffer (e.g., 1 minute) for "on time" status
    const timeBuffer = 1;

    const formatTimeDifference = (minutes) => {
      const days = Math.floor(minutes / (24 * 60));
      const remainingMinutes = minutes % (24 * 60);
      const hours = Math.floor(remainingMinutes / 60);
      const mins = remainingMinutes % 60;

      const parts = [];

      if (days > 0) {
        parts.push(`${days} ${days > 1 ? language.DAYS : language.DAY}`);
      }
      if (hours > 0) {
        parts.push(
          `${hours} ${hours > 1 ? language.HOURS_SHORT : language.HOUR_SHORT}`,
        );
      }
      if (mins > 0) {
        parts.push(
          `${mins} ${mins > 1 ? language.MINS_SHORT : language.MIN_SHORT}`,
        );
      }

      return parts.join(" ");
    };

    if (Math.abs(diffInMinutes) <= timeBuffer) {
      return { color: "#43a047", text: language.ON_TIME };
    } else if (diffInMinutes < -timeBuffer) {
      // Early case: convert negative minutes to positive for display
      const earlyBy = Math.abs(diffInMinutes);
      const difference = formatTimeDifference(earlyBy);
      return { color: "#1e88e5", text: `${difference} ${language.EARLY}` };
    } else {
      // Late case
      const difference = formatTimeDifference(diffInMinutes);
      return { color: "#e53935", text: `${difference} ${language.LATE}` };
    }
  };

  const checkDropTime = () => {
    const estimationTime = value?.estimation?.time;
    const bookedEstimationTime = value?.bookedEstimation?.time;
    const differenceInSeconds = bookedEstimationTime - estimationTime;
    const differenceInMinutes = Math.abs(
      +(differenceInSeconds / 60).toFixed(1),
    );

    const formatTimeDifference = (minutes) => {
      const days = Math.floor(minutes / (24 * 60));
      const remainingMinutes = minutes % (24 * 60);
      const hours = Math.floor(remainingMinutes / 60);
      const mins = Math.round(remainingMinutes % 60);

      const parts = [];

      if (days > 0) {
        parts.push(`${days} ${days > 1 ? language.DAYS : language.DAY}`);
      }
      if (hours > 0) {
        parts.push(
          `${hours} ${hours > 1 ? language.HOURS_SHORT : language.HOUR_SHORT}`,
        );
      }
      if (mins > 0 || parts.length === 0) {
        // Include minutes if it's the only value
        parts.push(
          `${mins} ${mins > 1 ? language.MINS_SHORT : language.MIN_SHORT}`,
        );
      }

      return parts.join(" ");
    };

    if (differenceInSeconds > 0) {
      if (differenceInMinutes <= 1) {
        return {
          completed: true,
          color: "#43a047",
          text: language.ON_TIME,
        };
      } else {
        const formattedTime = formatTimeDifference(differenceInMinutes);
        return {
          completed: true,
          color: "#1e88e5",
          text: `${formattedTime} ${language.MIN_EARLIER_THAN_EXPECTED}`,
        };
      }
    } else {
      const formattedTime = formatTimeDifference(differenceInMinutes);
      return {
        completed: true,
        color: "#e53935",
        text: `${formattedTime} ${language.MIN_LATE_THAN_EXPECTED}`,
      };
    }
  };

  const calculateCurvedPath = (start, end, numPoints = 50) => {
    const curvedPath = [];

    // Calculate the midpoint between start and end
    const midPoint = {
      lat: (start.lat + end.lat) / 2,
      lng: (start.lng + end.lng) / 2,
    };

    // Calculate the distance between points
    const distance = Math.sqrt(
      Math.pow(end.lat - start.lat, 2) + Math.pow(end.lng - start.lng, 2),
    );

    // Adjust the curve height based on the distance
    const curveHeight = distance * 0.4;

    for (let i = 0; i <= numPoints; i++) {
      const t = i / numPoints;

      // Quadratic bezier curve formula
      const lat =
        Math.pow(1 - t, 2) * start.lat +
        2 * (1 - t) * t * (midPoint.lat + curveHeight) +
        Math.pow(t, 2) * end.lat;

      const lng =
        Math.pow(1 - t, 2) * start.lng +
        2 * (1 - t) * t * midPoint.lng +
        Math.pow(t, 2) * end.lng;

      curvedPath.push({ lat, lng });
    }

    return curvedPath;
  };

  useEffect(() => {
    if (isLoaded && map && value) {
      if (value?.rideCoordinatesEncoded) {
        const path = window.google.maps.geometry.encoding.decodePath(
          value?.rideCoordinatesEncoded,
        );
        setDirectionRoutePoints(path);
      }
      //calculation for EXPECTEDPICKUPTIME
      if (
        value?.bookingStatus === "ENDED" &&
        value?.activity?.pickUpLatLng?.lat &&
        value?.activity?.pickUpLatLng?.lng &&
        value?.activity?.acceptLatLng?.lat &&
        value?.activity?.acceptLatLng?.lng
      ) {
        const destinations = [
          {
            lat: parseFloat(value?.activity?.pickUpLatLng?.lat),
            lng: parseFloat(value?.activity?.pickUpLatLng?.lng),
          },
        ];
        directionRoute(
          value?.activity?.acceptLatLng?.lat,
          value?.activity?.acceptLatLng?.lng,
          destinations,
          "EXPECTEDPICKUPTIME",
        );
      } else if (
        value?.rideStops?.[0]?.lat &&
        value?.rideStops?.[0]?.lng &&
        value?.activity?.acceptLatLng?.lat &&
        value?.activity?.acceptLatLng?.lng
      ) {
        const destinations = [
          {
            lat: parseFloat(value?.activity?.rideStops?.[0]?.lat),
            lng: parseFloat(value?.activity?.rideStops?.[0]?.lng),
          },
        ];
        directionRoute(
          value?.activity?.acceptLatLng?.lat,
          value?.activity?.acceptLatLng?.lng,
          destinations,
          "EXPECTEDPICKUPTIME",
        );
      }
    }
  }, [isLoaded, value, map]);

  useEffect(() => {
    //curve path from accpt to arrive when pickupwaydata is empty
    if (value?.pickupWayData?.length === 0) {
      if (
        value?.activity?.acceptLatLng?.lat &&
        value?.activity?.acceptLatLng?.lng &&
        value?.activity?.pickUpLatLng?.lat &&
        value?.activity?.pickUpLatLng?.lng
      ) {
        const start = {
          lat: value?.activity?.acceptLatLng?.lat,
          lng: value?.activity?.acceptLatLng?.lng,
        };
        const end = {
          lat: value?.activity?.pickUpLatLng?.lat,
          lng: value?.activity?.pickUpLatLng?.lng,
        };
        const curvedPathPolyline = calculateCurvedPath(start, end);
        setCurvedPath(curvedPathPolyline);
      }
    }

    //getting interrupted manualway
    if (value?.manualWayData?.length > 0) {
      const filterInteruptedData = value?.manualWayData?.filter(
        (m) => m?.isInterrupt,
      );
      if (filterInteruptedData?.length > 0) {
        setInterruptedPath(filterInteruptedData);
      }
      const filterStops = value?.manualWayData?.filter((m) => m?.isStop);
      if (filterStops?.length > 0) {
        setStopsData(filterStops);
      }
    }
  }, [value]);

  // Add fullscreen change listener
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        centerMap();
        map.setZoom(map.getZoom() - 0.5);
      } else {
        centerMap();
      }
    };

    const mapContainer = mapContainerRef.current;
    if (mapContainer) {
      document.addEventListener("fullscreenchange", handleFullscreenChange);
    }

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [mapRef.current, value]);

  //custom zoom,fullscreen,traffic,recenter buttons
  React.useEffect(() => {
    if (mapRef.current) {
      // Create control divs
      const centerControlDiv = document.createElement("div");
      const fullScreenControlDiv = document.createElement("div");
      const trafficControlDiv = document.createElement("div");

      const zoomControlDiv = document.createElement("div");
      const identification = document.createElement("div");

      // Set classes
      centerControlDiv.className = "custom-map-control";
      fullScreenControlDiv.className = "custom-full-screen-control";
      trafficControlDiv.className = "custom-traffic-control";
      zoomControlDiv.className = "custom-zoom-control";

      identification.className = "custom-identification-control";

      // identification button
      const identificationButton = document.createElement("button");
      identificationButton.className = `rounded-lg p-2 px-4 shadow-md   `;
      identificationButton.style.marginTop = "11px";
      identificationButton.style.marginBottom = "1px";
      identificationButton.style.marginLeft = "11px";
      identificationButton.style.marginRight = "31px";
      identificationButton.style.background =
        value?.serviceCategory === "packages"
          ? "#ea580c"
          : value?.serviceCategory === "ride"
            ? "#16a34a"
            : value?.serviceCategory === "corporateride"
              ? "#4f46e5"
              : "#2563eb";
      identificationButton.style.cursor = "pointer";
      identificationButton.innerHTML =
        value?.serviceCategory === "packages"
          ? `<div title="package" >
           <center> 
             <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="18px" width="18px" xmlns="http://www.w3.org/2000/svg">
               <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
               <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
               <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
               <line x1="12" y1="22.08" x2="12" y2="12"></line>
             </svg>
           </center>
            <p> ${language.DELIVERY}</p>
         </div>`
          : value?.serviceCategory === "ride"
            ? `<div title="ride" class="flex items-center">
           <center class="mr-2">
             <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 1024 1024" height="18px" width="18px" xmlns="http://www.w3.org/2000/svg">
               <path d="M380 704h264c4.4 0 8-3.6 8-8v-84c0-4.4-3.6-8-8-8h-40c-4.4 0-8 3.6-8 8v36H428v-36c0-4.4-3.6-8-8-8h-40c-4.4 0-8 3.6-8 8v84c0 4.4 3.6 8 8 8zm340-123a40 40 0 1 0 80 0 40 40 0 1 0-80 0zm239-167.6L935.3 372a8 8 0 0 0-10.9-2.9l-50.7 29.6-78.3-216.2a63.9 63.9 0 0 0-60.9-44.4H301.2c-34.7 0-65.5 22.4-76.2 55.5l-74.6 205.2-50.8-29.6a8 8 0 0 0-10.9 2.9L65 413.4c-2.2 3.8-.9 8.6 2.9 10.8l60.4 35.2-14.5 40c-1.2 3.2-1.8 6.6-1.8 10v348.2c0 15.7 11.8 28.4 26.3 28.4h67.6c12.3 0 23-9.3 25.6-22.3l7.7-37.7h545.6l7.7 37.7c2.7 13 13.3 22.3 25.6 22.3h67.6c14.5 0 26.3-12.7 26.3-28.4V509.4c0-3.4-.6-6.8-1.8-10l-14.5-40 60.3-35.2a8 8 0 0 0 3-10.8zM840 517v237H184V517l15.6-43h624.8l15.6 43zM292.7 218.1l.5-1.3.4-1.3c1.1-3.3 4.1-5.5 7.6-5.5h427.6l75.4 208H220l72.7-199.9zM224 581a40 40 0 1 0 80 0 40 40 0 1 0-80 0z"></path>
             </svg>
              
           </center>
           <p> ${language.RIDE}</p>
         </div>`
            : value?.serviceCategory === "corporateride"
              ? `<div title="package" >
           <center> 
             <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="18px" width="18px" xmlns="http://www.w3.org/2000/svg"><path fill="none" d="M0 0h24v24H0z"></path><path d="M12 7V3H2v18h20V7H12zm-2 12H4v-2h6v2zm0-4H4v-2h6v2zm0-4H4V9h6v2zm0-4H4V5h6v2zm10 12h-8V9h8v10zm-2-8h-4v2h4v-2zm0 4h-4v2h4v-2z"></path></svg>
           </center>
            <p> ${language.CORPORATERIDE}</p>
         </div>`
              : null;

      // Recenter button
      const centerButton = document.createElement("button");
      centerButton.className =
        "bg-white rounded-full p-3 shadow-md dark:hover:bg-gray-600  hover:bg-gray-100 focus:outline-none";
      centerButton.style.marginTop = "11px";
      centerButton.style.marginBottom = "1px";
      centerButton.style.marginLeft = "11px";
      centerButton.style.marginRight = "11px";
      centerButton.style.cursor = "pointer";
      centerButton.innerHTML = `
        <svg width="18px" height="18px" viewBox="0 0 24 24" fill="none">
        <title>Center</title>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M20.9 10.5H24v3h-3.1c-.7 3.8-3.6 6.8-7.4 7.4V24h-3v-3.1c-3.8-.7-6.8-3.6-7.4-7.4H0v-3h3.1c.7-3.8 3.6-6.8 7.4-7.4V0h3v3.1c3.8.7 6.8 3.6 7.4 7.4ZM6 12c0 3.3 2.7 6 6 6s6-2.7 6-6-2.7-6-6-6-6 2.7-6 6Zm9 0a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
          fill="currentColor"
        ></path>
      </svg>
      `;

      // Fullscreen button
      const fullScreenButton = document.createElement("button");
      fullScreenButton.className =
        "bg-white rounded-md p-2 shadow-md dark:hover:bg-gray-600  hover:bg-gray-100 focus:outline-none";
      fullScreenButton.style.margin = "11px";
      fullScreenButton.style.cursor = "pointer";
      fullScreenButton.innerHTML = `
       <svg stroke="currentColor" fill="none" strokeWidth="0" viewBox="0 0 15 15" height="22px" width="22px" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M2 2.5C2 2.22386 2.22386 2 2.5 2H5.5C5.77614 2 6 2.22386 6 2.5C6 2.77614 5.77614 3 5.5 3H3V5.5C3 5.77614 2.77614 6 2.5 6C2.22386 6 2 5.77614 2 5.5V2.5ZM9 2.5C9 2.22386 9.22386 2 9.5 2H12.5C12.7761 2 13 2.22386 13 2.5V5.5C13 5.77614 12.7761 6 12.5 6C12.2239 6 12 5.77614 12 5.5V3H9.5C9.22386 3 9 2.77614 9 2.5ZM2.5 9C2.77614 9 3 9.22386 3 9.5V12H5.5C5.77614 12 6 12.2239 6 12.5C6 12.7761 5.77614 13 5.5 13H2.5C2.22386 13 2 12.7761 2 12.5V9.5C2 9.22386 2.22386 9 2.5 9ZM12.5 9C12.7761 9 13 9.22386 13 9.5V12.5C13 12.7761 12.7761 13 12.5 13H9.5C9.22386 13 9 12.7761 9 12.5C9 12.2239 9.22386 12 9.5 12H12V9.5C12 9.22386 12.2239 9 12.5 9Z" fill="currentColor"></path></svg>
      `;

      // Traffic button
      const trafficButton = document.createElement("button");
      trafficButton.className =
        "bg-white rounded-lg p-3 px-3 shadow-md dark:hover:bg-gray-600  hover:bg-gray-100 focus:outline-none";
      trafficButton.style.margin = "11px";
      trafficButton.style.cursor = "pointer";
      trafficButton.style.fontSize = "14px";
      trafficButton.innerHTML = `
  <div class="flex items-center" style="gap: 8px;">
    <div class="rounded-full trafficActive" style="background-color: #e53935; height: 10px; width: 10px;"></div>
    <p>${language.TRAFFIC}</p>
  </div>
`;

      // Zoom controls
      const zoomInButton = document.createElement("button");
      const zoomOutButton = document.createElement("button");

      zoomInButton.className = `bg-white rounded-t-lg p-3 shadow-md dark:hover:bg-gray-600  hover:bg-gray-100 focus:outline-none border-b ${themeT.colors.borderColor} border-gray-200`;
      zoomOutButton.className =
        "bg-white rounded-b-lg p-3 shadow-md dark:hover:bg-gray-600  hover:bg-gray-100 focus:outline-none";

      zoomInButton.style.cursor = "pointer";
      zoomOutButton.style.cursor = "pointer";

      zoomInButton.innerHTML = `
        <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none">
          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
        </svg>
      `;

      zoomOutButton.innerHTML = `
        <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none">
          <path d="M19 13H5v-2h14v2z" fill="currentColor"/>
        </svg>
      `;

      // Add event listeners
      centerButton.addEventListener("click", centerMap);

      fullScreenButton.addEventListener("click", () => {
        const mapDiv = map.getDiv();
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          mapDiv.requestFullscreen();
        }
      });

      let trafficLayer = null;
      const indicator = trafficButton.querySelector("div > div");
      trafficButton.addEventListener("click", () => {
        if (trafficLayer) {
          trafficLayer.setMap(null);
          trafficLayer = null;
          trafficButton.style.backgroundColor =
            theme === "light" ? "#fff" : "#1e1e1e";
          indicator.style.backgroundColor = "#e53935";
        } else {
          trafficLayer = new window.google.maps.TrafficLayer();
          trafficLayer.setMap(map);
          trafficButton.style.backgroundColor =
            theme === "light" ? "#f5f5f5" : "#595959";
          indicator.style.backgroundColor = "#43a047";
        }
      });

      if (pathname) {
        const splitted = pathname?.split("/");
        if (splitted[splitted?.length - 1] === "ongoing") {
          trafficLayer = new window.google.maps.TrafficLayer();
          trafficLayer.setMap(map);
          trafficButton.style.backgroundColor = "#f5f5f5";
          indicator.style.backgroundColor = "#43a047";
        }
      }

      zoomInButton.addEventListener("click", () => {
        map.setZoom(map.getZoom() + 1);
      });

      zoomOutButton.addEventListener("click", () => {
        map.setZoom(map.getZoom() - 1);
      });

      // Append buttons to their containers
      centerControlDiv.appendChild(centerButton);
      fullScreenControlDiv.appendChild(fullScreenButton);
      trafficControlDiv.appendChild(trafficButton);

      zoomControlDiv.appendChild(zoomInButton);
      zoomControlDiv.appendChild(zoomOutButton);
      identification.appendChild(identificationButton);

      // Add controls to the map

      map.controls[window.google.maps.ControlPosition.RIGHT_TOP].push(
        fullScreenControlDiv,
      );
      map.controls[window.google.maps.ControlPosition.LEFT_BOTTOM].push(
        trafficControlDiv,
      );
      map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(
        zoomControlDiv,
      );
      map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(
        centerControlDiv,
      );

      map.controls[window.google.maps.ControlPosition.TOP_LEFT].push(
        identification,
      );

      // Add styles
      const style = document.createElement("style");
      style.textContent = `
        .custom-map-control button,
        .custom-full-screen-control button,
        .custom-traffic-control button,
         .custom-toll-control button,
        .custom-zoom-control button,
        .custom-driver-control button {
          border: none;
          background: ${theme !== "light" ? "#1e1e1e" : "white"};
          color: ${theme !== "light" ? "white" : "black"};
          box-shadow: ${theme !== "light" ? "rgba(255, 255, 255, 0.16) 0px 4px 20px" : "rgb(0 0 0 / 30%) 0px 1px 4px -1px"};
          transition: background-color 0.3s ease;
        }
      
        .custom-identification-control button {
          border: none;
          background: ${theme !== "light" ? "#444" : "white"};
          color: white;
          box-shadow: ${theme !== "light" ? "rgba(255, 255, 255, 0.16) 0px 4px 20px" : "rgb(0 0 0 / 30%) 0px 1px 4px -1px"};
          transition: background-color 0.3s ease;
        }
      
        .custom-zoom-control {
          margin: 11px;
          display: flex;
          flex-direction: column;
        }
      
        .custom-zoom-control button {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      
        .custom-map-control {
          margin-bottom: 0px;
        }
      `;

      document.head.appendChild(style);

      return () => {
        document.head.removeChild(style);
        if (trafficLayer) {
          trafficLayer.setMap(null);
        }
      };
    }
  }, [mapRef.current, theme]);

  React.useEffect(() => {
    if (liveData?.locationUpdatedTime) {
      const formattedValue = format(
        new Date(liveData?.locationUpdatedTime),
        "MMM d, yyyy p",
      );
      setlocationUpdateTime(formattedValue);
    }
  }, [liveData]);

  if (!isLoaded) return <SmallLoader />;

  const BookingHistorySection = (isMobile = false) => {
    return (
      <section>
        <div
          className={` ${themeT.colors.blocksBg}   ${!isMobile ? `border  rounded-lg p-[25px] lg:ml-[10px] h-[calc(100vh-280px)] lg:h-full lg:w-[350px] overflow-auto w-full ${themeT.colors.borderColor} ${themeT.colors.dashBoardShadow}` : `border-none   lg:ml-[0px] ${isScrolled ? "overflow-auto  lg:h-full" : "h-full overflow-hidden"} h-[calc(100vh-90px)] lg:w-full  w-full pb-16`}`}
          //style={{ padding: 25, marginLeft: "8px", height: "450px", width: "350px", overflow: "auto" }}
        >
          {isMobile && (
            <div className={`border-b pb-7 mb-5 ${themeT.colors.borderColor}`}>
              {estimation}
            </div>
          )}
          <div className="flex justify-between items-center lg:px-8">
            <p className={` ${isMobile ? "text-lg font-bold" : "text-xs"}`}>
              {language.BOOKING_HISTORY}
            </p>
          </div>
          <div className="mt-3 lg:px-8" style={{ fontSize: "13px" }}>
            {value?.activity?.bookingTime && (
              <div className="flex items-center gap-3 w-full">
                <p>
                  <FaCheckCircle style={{ color: "green" }} />
                </p>
                <p>
                  {language.ORDER_CREATED} {language.ON || "On"}{" "}
                  {format(
                    new Date(value?.activity?.bookingTime),
                    "MMM d, yyyy",
                  )}{" "}
                  {language.AT}{" "}
                  {format(new Date(value?.activity?.bookingTime), "h:mm a")}
                </p>
              </div>
            )}

            {value?.bookingType === "SCHEDULE" && (
              <div className="flex items-center gap-3 mt-3 w-full">
                <p>
                  <svg
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    viewBox="0 0 24 24"
                    height="16px"
                    width="16px"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M7 3V1H9V3H15V1H17V3H21C21.5523 3 22 3.44772 22 4V9H20V5H17V7H15V5H9V7H7V5H4V19H10V21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H7ZM17 12C14.7909 12 13 13.7909 13 16C13 18.2091 14.7909 20 17 20C19.2091 20 21 18.2091 21 16C21 13.7909 19.2091 12 17 12ZM11 16C11 12.6863 13.6863 10 17 10C20.3137 10 23 12.6863 23 16C23 19.3137 20.3137 22 17 22C13.6863 22 11 19.3137 11 16ZM16 13V16.4142L18.2929 18.7071L19.7071 17.2929L18 15.5858V13H16Z"></path>
                  </svg>
                </p>
                <p>
                  {language.SCHEDULED_FOR}{" "}
                  {format(new Date(value?.bookingDate), "MMM d yyyy, h:mm a")}
                  <span></span>
                </p>
              </div>
            )}

            {value?.activity?.acceptTime && (
              <div className="flex items-center gap-3 mt-3 w-full">
                <AcceptIconMarker />
                <div>
                  <p>
                    {language.DRIVER_DEPARTED} {language.ON || "on"}{" "}
                    {format(
                      new Date(value?.activity?.acceptTime),
                      "MMM d, yyyy",
                    )}{" "}
                    {language.AT}{" "}
                    {format(new Date(value?.activity?.acceptTime), "h:mm a")}{" "}
                    {expectedPickup?.value >= 0 &&
                      `(${language.ESTIMATED_PICKUP_STATUS} ${formatTimeFromSeconds(expectedPickup?.value)})`}
                  </p>
                </div>
              </div>
            )}

            {value?.activity?.arriveTime && (
              <div className="flex items-center gap-3 mt-3 w-full">
                <ArriveIconMarker />
                <p>
                  {language.ARRIVED} {language.ON || "on"}{" "}
                  {format(new Date(value?.activity?.arriveTime), "MMM d, yyyy")}{" "}
                  {language.AT} {language.PICKUP} {language.AT}{" "}
                  {/* on {format(new Date(value?.activity?.acceptTime), "MMM d, yyyy")} at at{" "} */}
                  {format(new Date(value?.activity?.arriveTime), "h:mm a")}{" "}
                  {checkAcceptToPickup()?.text && (
                    <span
                      style={{ color: checkAcceptToPickup()?.color }}
                    >{`(${checkAcceptToPickup()?.text})`}</span>
                  )}
                </p>
              </div>
            )}

            {value?.invoice?.beforeRideWaitingMins > 0 && (
              <div className="flex items-center gap-3 mt-3 w-full">
                <svg
                  stroke="currentColor"
                  fill="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 256 256"
                  height="15px"
                  width="15px"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm29.66-93.66a8,8,0,0,1,0,11.32l-40,40a8,8,0,0,1-11.32-11.32L140.69,128,106.34,93.66a8,8,0,0,1,11.32-11.32Z"></path>
                </svg>
                <p>
                  {language.WAITING} {language.AT} {language.PICKUP}{" "}
                  {language.FOR}{" "}
                  {value?.invoice?.beforeRideWaitingMins &&
                    formatTimeFromSeconds(
                      Number(value?.invoice?.beforeRideWaitingMins) * 60,
                    )}
                </p>
              </div>
            )}

            {value?.activity?.pickUpTime && (
              <div className="flex items-center gap-3 mt-3 w-full">
                <PickupFinalIconMarker />
                <p>
                  {language.RIDE} {language.STARTED} on{" "}
                  {format(new Date(value?.activity?.pickUpTime), "MMM d, yyyy")}{" "}
                  {language.AT}{" "}
                  {format(new Date(value?.activity?.pickUpTime), "h:mm a")}
                </p>
              </div>
            )}

            {value?.activity?.stopTime?.length > 0 && (
              <>
                <p className="mt-3">{language.STOPS}:</p>
                {value?.activity?.stopTime?.map((s, i) => (
                  <>
                    <div className="flex items-center gap-3 mt-2 w-full">
                      <StopsIconMarker value={i + 1} />
                      <p>
                        {language.STOP} {i + 1}{" "}
                        {/* on {format(new Date(s), "MMM d, yyyy")} */}
                        {language.AT} {format(new Date(s), "h:mm a")}
                      </p>
                    </div>
                  </>
                ))}
              </>
            )}

            {value?.invoice?.afterRideWaitingMins > 0 && (
              <div className="flex items-center gap-3 mt-3 w-full">
                <svg
                  stroke="currentColor"
                  fill="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 256 256"
                  height="15px"
                  width="15px"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm29.66-93.66a8,8,0,0,1,0,11.32l-40,40a8,8,0,0,1-11.32-11.32L140.69,128,106.34,93.66a8,8,0,0,1,11.32-11.32Z"></path>
                </svg>
                <p>
                  {language.WAITED_DURING_RIDE_FOR}{" "}
                  {value?.invoice?.afterRideWaitingMins &&
                    formatTimeFromSeconds(
                      Number(value?.invoice?.afterRideWaitingMins) * 60,
                    )}
                </p>
              </div>
            )}

            {value?.activity?.cancelTime && (
              <div className="flex items-center gap-3 mt-3 w-full">
                <p>
                  <AiFillCloseCircle size={15} style={{ color: "#f44336" }} />
                </p>
                <p>
                  {value?.bookingStatus === "USERCANCELLED"
                    ? language.PASSENGER
                    : value?.bookingStatus === "PROFESSIONALCANCELLED"
                      ? language.PROFESSIONAL
                      : language.PASSENGER}{" "}
                  {language.CANCELLED} {language.AT}{" "}
                  {format(new Date(value?.activity?.cancelTime), "h:mm a")}
                </p>
              </div>
            )}

            {value?.activity?.denyOrExpireTime && (
              <div className="flex items-center gap-3 mt-3 w-full">
                <p>
                  <AiFillCloseCircle size={15} style={{ color: "#f44336" }} />
                </p>
                <p>
                  {value?.bookingStatus === "USERDENY"
                    ? language.PASSENGER + " " + language.DENIED
                    : language.EXPIRED}{" "}
                  {language.ON || "on"}{" "}
                  {format(
                    new Date(value?.activity?.denyOrExpireTime),
                    "MMM d, yyyy",
                  )}{" "}
                  {language.AT}{" "}
                  {format(
                    new Date(value?.activity?.denyOrExpireTime),
                    "h:mm a",
                  )}
                </p>
              </div>
            )}

            {value?.bookingStatus === "EXPIRED" && (
              <div className="flex items-center gap-3 mt-3 w-full">
                <p>
                  <AiFillCloseCircle size={15} style={{ color: "#f44336" }} />
                </p>
                <p>{language.REQ_EXPIRED}</p>
              </div>
            )}

            {value?.activity?.dropTime && (
              <div className="flex items-center gap-3 mt-3 w-full">
                <p>
                  <DropFinalIconMarker />
                </p>
                <p>
                  {language.RIDE_COMPLETED}{" "}
                  {value?.activity?.endedBy?.userType === "ADMIN" &&
                    language.BY + " " + language.ADMIN}{" "}
                  {language.ON}{" "}
                  {format(new Date(value?.activity?.dropTime), "MMM d, yyyy")}{" "}
                  {language.AT}{" "}
                  {format(new Date(value?.activity?.dropTime), "h:mm a")}
                  {"  "}{" "}
                  <span
                    className=""
                    style={{ color: checkDropTime()?.color }}
                  >{`  (${checkDropTime()?.text}) `}</span>
                </p>
              </div>
            )}
          </div>

          <hr
            className={`mt-5 lg:ml-8 lg:mr-8  border ${themeT.colors.borderColor}`}
          />

          <div className="mt-5 lg:px-8 text-xs font-medium">
            {/* <p>Route Details</p> */}

            <div className="flex items-center gap-4 mt-3">
              <div
                className=" rounded-full"
                style={{
                  border: "1.6px solid #43a047",
                  backgroundColor: theme === "light" ? "#e8f5e9" : "#1b2e1d",
                  width: "21px",
                  height: "7px",
                }}
              ></div>
              <p>{language.ESTIMATED_ROUTE}</p>
            </div>

            {liveData ? (
              <div className="flex items-center gap-3 mt-3">
                <div className="flex items-center" style={{ gap: "3px" }}>
                  <div
                    className=" rounded-full"
                    style={{
                      backgroundColor: theme === "light" ? "#000" : "#9fb0b8",
                      width: "11px",
                      height: "2px",
                    }}
                  ></div>
                  <div
                    className=" rounded-full"
                    style={{
                      backgroundColor: theme === "light" ? "#000" : "#9fb0b8",
                      width: "11px",
                      height: "2px",
                    }}
                  ></div>
                </div>
                <p>{language.ACTUAL_ROUTE_TO_PICKUP}</p>
              </div>
            ) : value?.pickupWayData?.length > 0 ? (
              <div className="flex items-center gap-3 mt-3">
                <div className="flex items-center" style={{ gap: "3px" }}>
                  <div
                    className=" rounded-full"
                    style={{
                      backgroundColor: theme === "light" ? "#000" : "#9fb0b8",
                      width: "11px",
                      height: "2px",
                    }}
                  ></div>
                  <div
                    className=" rounded-full"
                    style={{
                      backgroundColor: theme === "light" ? "#000" : "#9fb0b8",
                      width: "11px",
                      height: "2px",
                    }}
                  ></div>
                </div>
                <p>{language.ACTUAL_ROUTE_TO_PICKUP}</p>
              </div>
            ) : (
              <div className="flex items-center gap-3 mt-3 ">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 100 60"
                  width="25"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="5"
                >
                  <path d="M10 40 Q50 10, 90 40" />
                </svg>
                <p>{language.ACTUAL_ROUTE_TO_PICKUP}</p>
              </div>
            )}

            <div className="flex items-center gap-4 mt-3">
              <div
                className=" rounded-full"
                style={{
                  backgroundColor: "#f44336",
                  width: "21px",
                  height: "7px",
                }}
              ></div>
              <p>{language.LOCATION_TRACKING_DISRUPTION}</p>
            </div>

            <div className="flex items-center gap-4 mt-3 ">
              <div
                className=" rounded-full"
                style={{
                  backgroundColor: theme === "light" ? "#263238" : "#adadad",
                  width: "21px",
                  height: "7px",
                }}
              ></div>
              <p>{language.ACTUAL_ROUTE_TAKEN}</p>
            </div>

            <div
              className="flex items-center mt-3 ml-1"
              style={{ gap: "26px" }}
            >
              <div className="flex items-center gap-4 ">
                <p>
                  {/* <FaCheckCircle style={{ color: "green" }} /> */}
                  <PickupEstimatedIconMarker />
                </p>
                <p>{language.ESTIMATED_PICKUP}</p>
              </div>
              <div className="flex items-center gap-4">
                <p>
                  <DropEstimatedIconMarker />
                </p>
                <p>{language.ESTIMATED_DROP}</p>
              </div>
            </div>

            <div
              className="flex items-center mt-3 ml-1"
              style={{ gap: "48px" }}
            >
              <div className="flex items-center gap-4 ">
                <p>
                  <PickupFinalIconMarker />
                </p>
                <p>{language.ACTUAL_PICKUP}</p>
              </div>
              <div className="flex items-center gap-4">
                <p>
                  <DropFinalIconMarker />
                </p>
                <p>{language.ACTUAL_DROP}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const tabList = [
    // { id: 1, label: "History" },
    { id: 1, label: language.BOOKINGS },
    { id: 2, label: language.PASSENGER },
    { id: 3, label: language.DRIVER },
    { id: 4, label: language.CONTACT || "Contact" },
  ];

  return (
    <>
      <section className="h-full w-full px-0 xl:px-8 lg:px-6 2xl:px-36">
        <section className="lg:flex lg:justify-between xl:gap-5 lg:gap-3 2xl:gap-6 lg:items-start">
          <div
            className={` fixed lg:relative top-[60px] lg:top-0 w-full   lg:w-7/12 `}
          >
            <div className="relative h-[520px] lg:h-full">
              <div className={` w-full h-full`} ref={mapContainerRef}>
                <GoogleMap
                  options={{
                    styles: theme === "light" ? mapStyles : darkModeMapStyles,
                    zoomControl: false,
                    scrollwheel: true,
                    draggable: true,
                    gestureHandling: "cooperative",
                    disableDefaultUI: false,
                    keyboardShortcuts: false,
                    fullscreenControl: false,
                    mapTypeControl: false,
                    streetViewControl: false,
                    clickableIcons: false,
                    cameraControl: false,
                  }}
                  mapContainerStyle={{
                    width: "100%",
                    height:
                      window.innerWidth < 768 ? "100%" : "calc(100vh - 120px)",
                    borderRadius: window.innerWidth < 768 ? "0px" : "8px",
                    marginBottom: window.innerWidth < 768 ? "0px" : "18px",
                    position: "relative",
                    zIndex: 10,
                    boxShadow:
                      theme === "light"
                        ? "1px 4px 20px rgba(0,0,0,0.01) "
                        : "0px 4px 20px rgba(255,255,255,0.16)",
                  }}
                  onLoad={handleMapLoad}
                  onZoomChanged={handleZoomChanged}
                >
                  {value?.activity?.acceptLatLng && (
                    <MarkerF
                      position={getAdjustedPosition(
                        value?.activity?.acceptLatLng?.lat,
                        value?.activity?.acceptLatLng?.lng,
                        1, // Index for the first marker
                      )}
                      onClick={() => setShowPoAccept(!showPopAccept)}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 1,
                      }}
                    >
                      <OverlayViewF
                        position={getAdjustedPosition(
                          value?.activity?.acceptLatLng?.lat,
                          value?.activity?.acceptLatLng?.lng,
                          0, // Index for the first marker
                        )}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div
                          className="absolute bottom-0 top-0"
                          style={{ zIndex: 999 }}
                        >
                          <AcceptIconMarker
                            isPop
                            clicked={() => setShowPoAccept(!showPopAccept)}
                          />
                        </div>
                        {showPopAccept && (
                          <MarkerPop
                            marker={<AcceptIconMarker />}
                            content={
                              <p>
                                {language.DRIVER_DEPARTED} {language.AT}{" "}
                                {format(
                                  new Date(value?.activity?.acceptTime),
                                  "h:mm a",
                                )}{" "}
                                {`(${language.ESTIMATED_PICKUP_STATUS} ${expectedPickup?.text})`}
                              </p>
                            }
                            clickedClose={() => setShowPoAccept(false)}
                          />
                        )}
                      </OverlayViewF>
                    </MarkerF>
                  )}

                  {value?.activity?.arriveLatLng && (
                    <MarkerF
                      position={getAdjustedPosition(
                        value?.activity?.arriveLatLng?.lat,
                        value?.activity?.arriveLatLng?.lng,
                        1, // Index for the first marker
                      )}
                      onClick={() => setShowPopArrive(true)}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 0,
                      }}
                    >
                      <OverlayViewF
                        position={getAdjustedPosition(
                          value?.activity?.arriveLatLng?.lat,
                          value?.activity?.arriveLatLng?.lng,
                          1, // Index for the first marker
                        )}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div
                          className="absolute bottom-0 top-0"
                          style={{ zIndex: 9999 }}
                        >
                          <ArriveIconMarker
                            isPop
                            clicked={() => setShowPopArrive(true)}
                            map
                          />
                        </div>
                        {showPopArrive && (
                          <MarkerPop
                            marker={<ArriveIconMarker map />}
                            content={
                              <p>
                                {language.ARRIVED} {language.AT}{" "}
                                {/* on {format(new Date(value?.activity?.acceptTime), "MMM d, yyyy")} at at{" "} */}
                                {format(
                                  new Date(value?.activity?.arriveTime),
                                  "h:mm a",
                                )}{" "}
                                {checkAcceptToPickup()?.text && (
                                  <span
                                    style={{
                                      color: checkAcceptToPickup()?.color,
                                    }}
                                  >{`(${checkAcceptToPickup()?.text})`}</span>
                                )}
                              </p>
                            }
                            clickedClose={() => setShowPopArrive(false)}
                          />
                        )}
                      </OverlayViewF>
                    </MarkerF>
                  )}

                  {value?.activity?.pickUpTime && (
                    <MarkerF
                      position={getAdjustedPosition(
                        value?.activity?.pickUpLatLng?.lat,
                        value?.activity?.pickUpLatLng?.lng,
                        1, // Index for the first marker
                      )}
                      onClick={() => setShowPopStart(true)}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 0,
                      }}
                    >
                      <OverlayViewF
                        position={getAdjustedPosition(
                          value?.activity?.pickUpLatLng?.lat,
                          value?.activity?.pickUpLatLng?.lng,
                          1,
                        )}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div
                          className="absolute bottom-0 top-0"
                          style={{ zIndex: 9999 }}
                        >
                          <PickupFinalIconMarker
                            isPop
                            clicked={() => setShowPopStart(true)}
                          />
                        </div>
                        {showPopStart && (
                          <MarkerPop
                            marker={<PickupFinalIconMarker />}
                            content={
                              <p>
                                {language.RIDE} {language.STARTED}
                                {language.AT}{" "}
                                {format(
                                  new Date(value?.activity?.pickUpTime),
                                  "h:mm a",
                                )}
                              </p>
                            }
                            clickedClose={() => setShowPopStart(false)}
                          />
                        )}
                      </OverlayViewF>
                    </MarkerF>
                  )}

                  {value?.activity?.dropTime && value?.manualWayData && (
                    <MarkerF
                      position={
                        !value?.activity?.dropLatLng
                          ? getAdjustedPosition(
                              value?.dropLocation?.coordinates[0]?.[1],
                              value?.dropLocation?.coordinates[0]?.[0],
                              1, // Index for the first marker
                            )
                          : getAdjustedPosition(
                              value?.manualWayData[
                                value?.manualWayData?.length - 1
                              ]?.lat,
                              value?.manualWayData[
                                value?.manualWayData?.length - 1
                              ]?.lng,
                              1, // Index for the first marker
                            )
                      }
                      onClick={() => {
                        if (value?.activity?.dropTime) setShowFinalDrop(true);
                      }}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 0,
                      }}
                    >
                      <OverlayViewF
                        position={
                          !value?.activity?.dropLatLng
                            ? getAdjustedPosition(
                                value?.dropLocation?.coordinates[0]?.[1],
                                value?.dropLocation?.coordinates[0]?.[0],
                                1, // Index for the first marker
                              )
                            : getAdjustedPosition(
                                value?.manualWayData[
                                  value?.manualWayData?.length - 1
                                ]?.lat,
                                value?.manualWayData[
                                  value?.manualWayData?.length - 1
                                ]?.lng,
                                1, // Index for the first marker
                              )
                        }
                        onClick={() => {
                          if (value?.activity?.dropTime) setShowFinalDrop(true);
                        }}
                        icon={{
                          path: window.google.maps.SymbolPath.CIRCLE,
                          scale: 0,
                        }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <section
                          className="absolute bottom-0 top-0"
                          style={{ zIndex: 9999 }}
                        >
                          <section className=" relative">
                            <DropFinalIconMarker
                              isPop={value?.activity?.dropTime}
                              clicked={() => setShowFinalDrop(true)}
                            />
                          </section>
                        </section>
                        {showPopFinalDrop && (
                          <MarkerPop
                            marker={<DropFinalIconMarker />}
                            content={
                              <p>
                                {language.RIDE_COMPLETED}{" "}
                                {value?.activity?.endedBy?.userType ===
                                  "ADMIN" &&
                                  language.BY + " " + language.ADMIN}{" "}
                                {language.AT}{" "}
                                {format(
                                  new Date(value?.activity?.dropTime),
                                  "h:mm a",
                                )}
                                {"  "}{" "}
                                <span
                                  className=""
                                  style={{ color: checkDropTime()?.color }}
                                >{`  (${checkDropTime()?.text}) `}</span>
                              </p>
                            }
                            clickedClose={() => setShowFinalDrop(false)}
                          />
                        )}
                      </OverlayViewF>
                    </MarkerF>
                  )}

                  {pickupPoint && (
                    <MarkerF
                      position={
                        value?.bookingStatus === "ENDED"
                          ? getAdjustedPosition(
                              value?.pickupLocation?.coordinates?.[0]?.[1],
                              value?.pickupLocation?.coordinates?.[0]?.[0],
                              0,
                            )
                          : getAdjustedPosition(
                              pickupPoint?.lat,
                              pickupPoint?.lng,
                              1,
                            )
                      }
                      onClick={() => setShowPopPick(!showPopPick)}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 0,
                      }}
                    >
                      <OverlayViewF
                        position={
                          value?.bookingStatus === "ENDED"
                            ? getAdjustedPosition(
                                value?.pickupLocation?.coordinates?.[0]?.[1],
                                value?.pickupLocation?.coordinates?.[0]?.[0],
                                0,
                              )
                            : getAdjustedPosition(
                                pickupPoint?.lat,
                                pickupPoint?.lng,
                                1,
                              )
                        }
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <section className=" relative">
                          <PickupEstimatedIconMarker
                            isPop
                            clicked={() => setShowPopPick(!showPopPick)}
                          />

                          {showPopPick && (
                            <MarkerPop
                              marker={<PickupEstimatedIconMarker />}
                              content={
                                <p
                                  className="text-xs font-light flex flex-col "
                                  style={{ fontSize: "10.5px" }}
                                >
                                  <span>{language.ESTIMATED_PICKUP}:</span>
                                  <span> {pickupPoint?.shortAddress}</span>
                                </p>
                              }
                              clickedClose={() => setShowPopPick(false)}
                            />
                          )}
                        </section>
                      </OverlayViewF>
                    </MarkerF>
                  )}

                  {dropPoint?.map((v, index) => (
                    <MarkerF
                      key={index}
                      onClick={() => {
                        setShowPopDrop(true);
                        setShowPopDropIndex(index);
                      }}
                      position={
                        value?.bookingStatus === "ENDED" &&
                        v?.type === "DESTINATION"
                          ? getAdjustedPosition(
                              value?.dropLocation?.coordinates?.[0]?.[1],
                              value?.dropLocation?.coordinates?.[0]?.[0],
                              index,
                            )
                          : getAdjustedPosition(v?.lat, v?.lng, 1)
                      }
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 0,
                      }}
                    >
                      <OverlayViewF
                        position={
                          value?.bookingStatus === "ENDED" &&
                          v?.type === "DESTINATION"
                            ? getAdjustedPosition(
                                value?.dropLocation?.coordinates?.[0]?.[1],
                                value?.dropLocation?.coordinates?.[0]?.[0],
                                index,
                              )
                            : getAdjustedPosition(v?.lat, v?.lng, 1)
                        } // Adjust position based on index
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        {v?.type === "STOP" ? (
                          <section
                            className="absolute bottom-0 top-0"
                            style={{ zIndex: 9999 }}
                          >
                            <StopsIconMarker
                              isPop
                              clicked={() => {
                                setShowPopDrop(true);
                                setShowPopDropIndex(index);
                              }}
                              value={index + 1}
                            />
                          </section>
                        ) : (
                          <section className=" relative">
                            <DropEstimatedIconMarker
                              isPop
                              clicked={() => {
                                setShowPopDrop(true);
                                setShowPopDropIndex(index);
                              }}
                            />
                          </section>
                        )}

                        {showPopDrop && index === showPopDropIndex && (
                          <div>
                            {dropPoint?.[showPopDropIndex]?.type === "STOP" ? (
                              <MarkerPop
                                marker={
                                  <StopsIconMarker
                                    value={showPopDropIndex + 1}
                                  />
                                }
                                content={
                                  <div>
                                    {" "}
                                    <p
                                      className="text-xs font-light flex flex-col "
                                      style={{ fontSize: "10.5px" }}
                                    >
                                      <span>
                                        {language.STOP}: {showPopDropIndex + 1}
                                      </span>
                                      <span>
                                        {" "}
                                        {dropPoint?.[showPopDropIndex]
                                          ?.shortAddress || "-"}
                                      </span>
                                    </p>
                                    {value?.activity?.stopTime && (
                                      <p>
                                        {language.STOP} {showPopDropIndex + 1}{" "}
                                        {/* on {format(new Date(s), "MMM d, yyyy")} */}
                                        {language.AT}{" "}
                                        {format(
                                          new Date(
                                            value?.activity?.stopTime?.[
                                              showPopDropIndex
                                            ],
                                          ),
                                          "h:mm a",
                                        )}
                                      </p>
                                    )}
                                  </div>
                                }
                                clickedClose={() => {
                                  setShowPopDrop(false);
                                  setShowPopDropIndex(0);
                                }}
                              />
                            ) : (
                              <MarkerPop
                                marker={<DropEstimatedIconMarker />}
                                content={
                                  <div>
                                    {" "}
                                    <p
                                      className="text-xs font-light flex flex-col "
                                      style={{ fontSize: "10px" }}
                                    >
                                      <span>{language.ESTIMATED_DROP}:</span>
                                      <span>
                                        {" "}
                                        {
                                          dropPoint?.[showPopDropIndex]
                                            ?.shortAddress
                                        }
                                      </span>
                                    </p>
                                  </div>
                                }
                                clickedClose={() => {
                                  setShowPopDrop(false);
                                  setShowPopDropIndex(0);
                                }}
                              />
                            )}
                          </div>
                        )}
                      </OverlayViewF>
                    </MarkerF>
                  ))}

                  {directionRoutePoints && (
                    <>
                      {/* Border polyline */}
                      <PolylineF
                        path={directionRoutePoints}
                        options={{
                          strokeColor: "#43a047", // border color
                          strokeWeight: 16, // slightly larger for border
                          strokeOpacity: 1,
                          zIndex: 1,
                        }}
                      />

                      {/* Main polyline */}
                      <PolylineF
                        path={directionRoutePoints}
                        options={{
                          strokeColor:
                            theme === "light" ? "#e8f5e9" : "#1b2e1d", // main color
                          strokeWeight: 13,
                          strokeOpacity: 1,
                          zIndex: 2,
                        }}
                      />
                    </>
                  )}

                  {/* pickup path */}
                  {value?.bookingStatus === "ENDED" &&
                    value?.pickupWayData?.length > 0 && (
                      // <PolylineF
                      //   path={value?.pickupWayData}
                      //   options={{
                      //     strokeColor: "#455a64",
                      //     //strokeWeight: 4.5,
                      //     strokeOpacity: 0,
                      //     zIndex: 10,
                      //     icons: [
                      //       {
                      //         icon: lineSymbol,
                      //         offset: "0",
                      //         repeat: "7px",
                      //       },
                      //     ],
                      //   }}
                      // />
                      <LiveRoute points={value?.pickupWayData} dashed />
                    )}

                  {/* actual path */}
                  {value?.bookingStatus === "ENDED" &&
                    actualPathWayData?.length > 0 && (
                      <>
                        {actualPathWayData.map((point, index) => {
                          if (index === actualPathWayData.length - 1)
                            return null;

                          const segment = [point, actualPathWayData[index + 1]];

                          return (
                            <PolylineF
                              key={index}
                              path={segment}
                              options={{
                                strokeColor: point.isInterrupt
                                  ? "#f44336"
                                  : theme === "light"
                                    ? "#455a64"
                                    : "#adadad",
                                strokeWeight: 4.5,
                                strokeOpacity: 1,
                                zIndex: 5,
                              }}
                            />
                          );
                        })}
                      </>
                    )}
                  {/* toll data */}
                  {showTolls &&
                    tollsData?.length > 0 &&
                    !(
                      value?.bookingStatus === "USERCANCELLED" ||
                      value?.bookingStatus === "PROFESSIONALCANCELLED" ||
                      value?.bookingStatus === "EXPIRED" ||
                      value?.bookingStatus === "USERDENY"
                    ) && (
                      <>
                        {tollsData.map((toll, index) => {
                          // Improved polygon centroid calculation using proper geometric centroid
                          const calculatePolygonCentroid = (coordinates) => {
                            if (!coordinates || coordinates.length === 0)
                              return null;

                            // Use the first (outer) polygon
                            const polygon = coordinates[0];
                            if (!polygon || polygon.length < 3) return null;

                            let area = 0;
                            let centroidLat = 0;
                            let centroidLng = 0;

                            // Calculate centroid using the shoelace formula
                            for (let i = 0; i < polygon.length - 1; i++) {
                              const [lng1, lat1] = polygon[i];
                              const [lng2, lat2] = polygon[i + 1];

                              const crossProduct = lng1 * lat2 - lng2 * lat1;
                              area += crossProduct;
                              centroidLat += (lat1 + lat2) * crossProduct;
                              centroidLng += (lng1 + lng2) * crossProduct;
                            }

                            area *= 0.5;

                            if (Math.abs(area) < 1e-10) {
                              // Fallback to simple average if area is too small
                              let totalLat = 0,
                                totalLng = 0;
                              polygon.forEach(([lng, lat]) => {
                                totalLat += lat;
                                totalLng += lng;
                              });
                              return {
                                lat: totalLat / polygon.length,
                                lng: totalLng / polygon.length,
                              };
                            }

                            centroidLat /= 6 * area;
                            centroidLng /= 6 * area;

                            return {
                              lat: centroidLat,
                              lng: centroidLng,
                            };
                          };

                          // Calculate the center point for this toll zone
                          const centerPoint = calculatePolygonCentroid(
                            toll?.locationCoordinates?.coordinates,
                          ) || {
                            lat: toll.locationAddress?.lat || 0,
                            lng: toll.locationAddress?.lng || 0,
                          };

                          const isBlinking =
                            blinkingToll === (toll.id || index);
                          const tollKey = toll.id || index;

                          return (
                            <React.Fragment key={tollKey}>
                              {/* Render toll zone polygons with blinking effect */}
                              {toll?.locationCoordinates?.coordinates?.map(
                                (path, i) => (
                                  <PolygonF
                                    key={`zone-${index}-${i}`}
                                    paths={path.map(([lng, lat]) => ({
                                      lat,
                                      lng,
                                    }))}
                                    options={{
                                      strokeColor: isBlinking
                                        ? "#FF8C00"
                                        : "#FF8C00",
                                      strokeWeight: isBlinking ? 4 : 2,
                                      strokeOpacity: isBlinking ? 1 : 0.7,
                                      fillColor: isBlinking
                                        ? "#FFE5E5"
                                        : "#F9FAFB",
                                      fillOpacity: isBlinking ? 0.4 : 0.2,
                                      zIndex: isBlinking ? 15 : 5,
                                    }}
                                    //className={isBlinking ? "animate-pulse" : ""}
                                  />
                                ),
                              )}

                              {/* Toll name overlay centered in polygon */}
                              <OverlayViewF
                                position={centerPoint}
                                mapPaneName="overlayMouseTarget"
                              >
                                <div
                                  style={{
                                    transform: "translate(-50%, -50%)",
                                    zIndex: isBlinking
                                      ? 20 + index
                                      : 10 + index,
                                    pointerEvents: "auto", // Enable click events
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    handleTollNameClick(toll.id, index)
                                  }
                                >
                                  {map?.getZoom() <= 3 ? (
                                    // Small dot at low zoom
                                    <div
                                      style={{
                                        width: isBlinking ? "12px" : "8px",
                                        height: isBlinking ? "12px" : "8px",
                                        backgroundColor: isBlinking
                                          ? "#FF0000"
                                          : "#EF4444",
                                        borderRadius: "50%",
                                        border: "2px solid white",
                                        boxShadow: isBlinking
                                          ? "FF8C00"
                                          : "0 1px 3px rgba(0,0,0,0.3)",
                                        transition: "all 0.3s ease",
                                      }}
                                      //className={isBlinking ? "animate-ping" : ""}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        backgroundColor: isBlinking
                                          ? "#FF8C00"
                                          : "#1F2937",
                                        color: "white",
                                        padding: "4px 12px",
                                        borderRadius: "16px",
                                        fontSize:
                                          map?.getZoom() <= 13
                                            ? "4px"
                                            : map?.getZoom() === 14
                                              ? "8px"
                                              : map?.getZoom() === 15
                                                ? "9px"
                                                : "10px",
                                        fontWeight: "600",
                                        border: isBlinking
                                          ? "2px solid #FF8C00"
                                          : "1px solid white",
                                        boxShadow: isBlinking
                                          ? "0 0 15px rgba(255,140,0,0.4), 0 2px 6px rgba(0,0,0,0.2)"
                                          : "0 2px 6px rgba(0,0,0,0.2)",
                                        whiteSpace: "nowrap",
                                        maxWidth: "200px",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        transition: "all 0.3s ease",
                                        //transform: isBlinking ? "scale(1.1)" : "scale(1)",
                                      }}
                                      className={`capitalize ${isBlinking ? "" : ""}`}
                                    >
                                      {toll.tollName?.length > 30
                                        ? toll.tollName.substring(0, 30) + "..."
                                        : toll.tollName || `Toll ${index + 1}`}
                                    </div>
                                  )}
                                </div>
                              </OverlayViewF>
                            </React.Fragment>
                          );
                        })}
                      </>
                    )}
                  {/* accept to pickup */}
                  {curvedPath?.length > 0 && (
                    <PolylineF
                      path={curvedPath}
                      options={{
                        geodesic: false,
                        strokeColor: "#000000",
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        icons: [
                          {
                            icon: {
                              path: window.google.maps.SymbolPath
                                .FORWARD_CLOSED_ARROW,
                            },
                            offset: "50%",
                          },
                        ],
                      }}
                    />
                  )}

                  {/* live path  */}
                  {value?.bookingStatus !== "ENDED" &&
                    onGoingWayData?.length > 0 && (
                      // <PolylineF
                      //   path={onGoingWayData}
                      //   options={{
                      //     strokeColor: "#455a64",
                      //     //strokeWeight: 4.5,
                      //     strokeOpacity: 0,
                      //     zIndex: 10,
                      //     icons: [
                      //       {
                      //         icon: lineSymbol,
                      //         offset: "0",
                      //         repeat: "7px",
                      //       },
                      //     ],
                      //   }}
                      // />
                      <LiveRoute points={onGoingWayData} dashed />
                    )}

                  {/* live path  */}
                  {value?.bookingStatus !== "ENDED" &&
                    onGoingWayDataAfterPicked?.length > 0 && (
                      // <PolylineF
                      //   path={onGoingWayDataAfterPicked}
                      //   options={{
                      //     strokeColor: "#455a64",
                      //     strokeWeight: 4.5,
                      //     strokeOpacity: 1,
                      //     zIndex: 5,
                      //   }}
                      // />
                      <LiveRoute points={onGoingWayDataAfterPicked} />
                    )}

                  {liveData && !endStates?.includes(value?.bookingStatus) && (
                    <DriverMarkers
                      driver={liveData}
                      image={value?.vehicle?.vehicleCategoryMapImage}
                    />
                  )}
                </GoogleMap>
              </div>
            </div>
          </div>

          <section
            className={`  mt-[570px] lg:mt-0 snap-y lg:snap-none w-full h-full  lg:w-5/12 lg:h-[calc(100vh-120px)] overflow-auto lg:border ${themeT.colors.borderColor} lg:rounded-xl z-50`}
          >
            <section
              ref={historySectionRef}
              className={`w-full md:w-full   h-full snap-start `}
            >
              <section
                className={`${themeT.colors.blocksBg} rounded-xl  relative`}
              >
                <section className={`relative  pt-5 lg:pt-0   `}>
                  <span className="bg-[#e8e8e8] h-[4px] w-[48px] absolute top-[10px] rounded-full left-1/2 transform -translate-x-1/2 block lg:hidden xl:hidden"></span>
                  <div
                    className={`px-4 lg:sticky lg:top-0 lg:pt-5 pt-2 ${themeT.colors.blocksBg} w-full lg:z-50`}
                  >
                    <div
                      className={`grid grid-cols-4  w-full justify-center items-center place-content-center border-[1px] ${themeT.colors.activeBorder}   rounded-[8px] `}
                    >
                      {tabList?.map((t, i) => (
                        <span
                          onClick={() => setSelectedTab(t?.id)}
                          className={` cursor-pointer flex flex-col justify-center items-center w-full ${i === 3 ? "border-r-0" : "border-r-[1px]"} h-full py-[10px] text-sm ${themeT.colors.activeBorder} ${selectedTab === t?.id ? `${themeT.colors.darkerActiveBg} text-white ${t?.id === 1 ? "rounded-tl-[7px] rounded-bl-[7px]" : ""}` : ""}`}
                          key={i}
                        >
                          {t?.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  {selectedTab === 1 ? (
                    <div className="px-5 lg:px-0 mt-4 lg:mt-3  ">
                      {BookingHistorySection(true)}
                    </div>
                  ) : selectedTab === 2 ? (
                    passengerProfile
                  ) : selectedTab === 3 ? (
                    driverProfile
                  ) : selectedTab === 4 ? (
                    <div>{contactSection}</div>
                  ) : (
                    <div className="px-5 lg:px-0 mt-4 lg:mt-3">
                      {BookingHistorySection(true)}
                    </div>
                  )}
                </section>
              </section>
            </section>
          </section>
        </section>
      </section>
    </>
  );
}

export default GoogleMapComponent;
